import React from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'

import {
  Text,
  Box,
  Heading,
  Column,
  Row,
  HR,
  Link,
  Circle
} from '../components/styled'

import { colors } from '../components/styled/theme'

import Layout from '../components/Layout'

import SectionBanner from '../components/SectionBanner'
import Bridge from '../components/svg/Bridge'
import Image from '../components/Image'
import VideoTile from '../components/Video'

import city from '../static/videos/city-2.mp4'
import dew from '../static/videos/dew.mp4'

import aboutStatic from '../static/hero/about-static.png'
import biking from '../static/videos/city-morning.mp4'
import clouds from '../static/videos/clouds.mp4'

import forest2 from '../static/forest-2.jpg'

const team = [
  {
    name: 'Mike Fix',
    bio:
      'These days you will find Mike working with people over coffee in San Francisco.',
    img: require('../static/team/mike-fix.jpg'),
    links: {
      twitter: 'https://twitter.com/fixitup2',
      github: 'https://github.com/mfix22',
      email: 'mailto:mike@dawnlabs.io'
    }
  },
  {
    name: 'Brian Dennis',
    bio: `As merely a wannabe outdoor enthusiast, Brian usually just has his head in a book somewhere inside.`,
    img: require('../static/team/brian-dennis.jpg'),
    links: {
      twitter: 'https://twitter.com/_briandennis',
      github: 'https://github.com/briandennis',
      email: 'mailto:brian@dawnlabs.io'
    }
  },
  {
    name: 'Jake Dexheimer',
    bio:
      'Jake is probably either traveling, spending too much time on the internet, or missing his dog.',
    img: require('../static/team/jake-dexheimer.png'),
    links: {
      twitter: 'https://twitter.com/_jakedex',
      github: 'https://github.com/jakedex',
      email: 'mailto:jake@dawnlabs.io'
    }
  }
]

const SocialLink = styled(Link)`
  font-size: 1rem;
  color: ${colors.gray1};

  &:hover {
    text-decoration: underline;
  }
`

const Principle = ({ id, title, children, img, reverse }) => (
  <Row
    flexDirection={['column-reverse', reverse ? 'row-reverse' : 'row']}
    mb="11.75rem"
    justifyContent="space-between"
    alignItems="center"
  >
    <Column width={['100%', '50%']} justifyContent="center">
      <Box>
        <Heading.h3 id={id} textAlign={['center', 'left']} mb={5}>
          {title}
        </Heading.h3>
        <Text.p>{children}</Text.p>
      </Box>
    </Column>
    <Column mb={[6, 0]} width={['100%', '45%', '40%']}>
      {img}
    </Column>
  </Row>
)

const HeroDynamic = props => (
  <Column width="50%" position="relative" mt="15rem" pl={5} {...props}>
    <Box
      position="absolute"
      right="0.5rem"
      width="6.5rem"
      height="5rem"
      top="23.25rem"
      border={2}
      css={{
        background: colors.green
      }}
    >
      <VideoTile
        opacity={0.7}
        objectPosition="right"
        height="100%"
        src={dew}
        i={1}
      />
    </Box>
    <Box
      position="absolute"
      right={0}
      top="-9.75rem"
      width="13.75rem"
      height="6rem"
      border={2}
      css={{
        background: colors.blue
      }}
    >
      <VideoTile
        opacity={0.3}
        height="5.75rem"
        mixBlendMode="luminosity"
        objectPosition="left"
        src={city}
        i={4}
      />
    </Box>
    <Circle
      zIndex={-1}
      position="absolute"
      bg="yellow"
      top="-3rem"
      left="3.5rem"
      size="30.5rem"
    />
    <Bridge />
  </Column>
)

const HeroStatic = props => (
  <Column justifyContent="center" height="100%" {...props}>
    <img width="100%" src={aboutStatic} alt="About page artwork" />
  </Column>
)

const About = props => (
  <Layout location={props.location}>
    <Column width="100%" alignItems="center">
      <Helmet>
        <title>About — Dawn Labs</title>
      </Helmet>
      <Row
        width="100%"
        flexDirection={['column-reverse', 'row']}
        pt={['5.5rem', 'unset']}
        pb={[7, 'unset']}
        alignItems={['center', 'unset']}
        justifyContent={['flex-end', 'space-between']}
        height={['unset', '48rem']}
      >
        <Column
          justifyContent="center"
          width={['100%', '50%', '48%', '38%']}
          maxWidth={['unset', '340px', '360px', 'unset']}
        >
          <Heading.h1 mb={5}>About Us</Heading.h1>
          <Text.subheader>
            We&#39;re a close-knit group that enjoys designing with empathy,
            focusing on what matters, and exploring new frontiers.
          </Text.subheader>
        </Column>
        <HeroDynamic display={['none', 'none', 'flex']} />
        <HeroStatic
          width={['100%', '45%']}
          maxWidth={[200, 'unset']}
          mb={[7, 0]}
          display={['flex', 'flex', 'none']}
        />
      </Row>
      <HR />
      <SectionBanner color="black" mb={8} title="About">
        Dawn began as an experiment between three friends at the University of
        Wisconsin. With experience ranging from large tech companies to high
        growth startups, we found that by combining our skill sets, we could
        create digital products in a novel way.
        <br />
        <br />
        We’re a small but versatile team. We&#39;re cross-disciplinary and
        believe that blurring the boundaries between design and engineering
        results in better products. This means working closely with each other,
        as well as with our clients. Frequent, intimate communication leads to
        us better understanding your needs and how to meet them.
        <br />
        <br />
        While we sprung out of the midwest, today we&#39;re primarily a
        distributed team. At the moment, you’ll find us working out of Chicago
        and San Francisco. Outside of our client work, we enjoy giving back to
        the open source community along with dabbling in ventures of our own.
      </SectionBanner>
      <HR />
      <SectionBanner title="Purpose" />
      <Column alignSelf="flex-start" width={['100%', '47%']} mb={9}>
        <Text.p>
          Even though we&#39;re just getting started, we think it&#39;s
          important to talk about the ideas that inspire us and guide our
          broader organizational aspirations.
          <br />
          <br />
          From what we can tell, recent breakthroughs like the internet mean
          that fundamentally different connections, interactions, and systems
          are now possible, but not yet in place. This means we, as a society,
          are uniquely positioned in history to meaningfully impact the future.
          Dawn is our attempt to live that out.
          <br />
          <br />
          While we&#39;re certainly still figuring things out, here are some
          principles that help guide our journey:
        </Text.p>
      </Column>

      <Principle
        id="technology-as-a-means"
        title="Technology as a means, not an end"
        img={
          <VideoTile
            border={2}
            height={['20rem', '24rem']}
            src={biking}
            color={colors.blue}
          />
        }
        reverse="true"
      >
        With the immense pace of technological innovation, it can be easy to
        lose sight of the reason technology is valuable in the first place.
        Making sure our focus is correctly aligned requires taking a step back,
        striving to always keep in mind the people and communities we work to
        better.
      </Principle>

      <Principle
        id="creation-over-disruption"
        title="Creation over disruption"
        img={
          <Box.Border height={['20rem', '24rem']} bg={colors.green}>
            <Image opacity={0.3} src={forest2} alt="Forest" />
          </Box.Border>
        }
      >
        Building something new means impacting the old; however, we think the
        pendulum has swung too far towards idolizing upheaval.
        <br />
        <br />
        We know life demands working with imperfect information, but making a
        conscious, deliberate effort to think about the ways we affect the world
        is a step in the right direction.
      </Principle>

      <Principle
        id="positive-sums"
        reverse="true"
        title="Positive sums"
        img={
          <VideoTile
            border={2}
            height={['20rem', '24rem']}
            src={clouds}
            color="#F4A09C"
          />
        }
      >
        We try to partner with organizations to foster relationships and build
        communities, combining our unique capabilities to accomplish more than
        either group could on their own. We believe lasting partnerships matter
        and that playing the long-game together yields compounding benefits.
      </Principle>
      <HR />
      <SectionBanner title="Team" />
      <Row
        flexDirection={['column', 'column', 'row']}
        justifyContent="space-between"
        flexWrap="wrap"
        mb={[0, 0, 8]}
      >
        {team.map(member => (
          <Column
            mb={[9, 8, 0]}
            flexDirection={['column', 'row', 'column']}
            key={member.name}
            width={['100%', '100%', '31%']}
            maxWidth={[380, 'unset', 'unset']}
          >
            <Box
              mb={[5, 0, 5]}
              mr={[0, 7, 0]}
              maxWidth={['unset', 380, 'unset']}
              height={'21rem'}
              width={['100%', '100%', '100%', 'unset']}
              css={{ overflowY: 'hidden' }}
            >
              <img src={member.img} alt={member.name} width="100%" />
            </Box>
            <Column justifyContent={['center', 'center', 'unset']}>
              <Box mb={4}>
                <Text
                  fontSize="1.25rem"
                  display="inline-block"
                  fontWeight={600}
                  mr={5}
                >
                  {member.name}
                </Text>
                {Object.keys(member.links).map(link => (
                  <SocialLink key={link} mr={3} href={member.links[link]}>
                    {link}
                  </SocialLink>
                ))}
              </Box>
              <Text fontSize="1.25rem" lineHeight={1.4}>
                {member.bio}
              </Text>
            </Column>
          </Column>
        ))}
      </Row>
    </Column>
  </Layout>
)

export default About
